import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { getUserTier } from "./lib/quota";
import type { Capability } from "@/lib/quotas";
import type { Tier } from "@/hooks/useSubscription";
import type { Database } from "@/integrations/supabase/types";

export type UsageSummary = {
  tier: Tier;
  periodStart: string; // ISO date (1st of UTC month)
  counts: Record<Capability, number>;
};

function emptySummary(): UsageSummary {
  const periodStart = new Date(
    Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1),
  )
    .toISOString()
    .slice(0, 10);
  return { tier: "free", periodStart, counts: {} as Record<Capability, number> };
}

/**
 * Returns the user's current tier + monthly usage counts for ALL capabilities.
 *
 * NOTE: We intentionally do NOT use `requireSupabaseAuth` middleware here
 * because that middleware throws a raw `Response` on missing/invalid token,
 * which surfaces as a `[object Response]` unhandled rejection on the client
 * during the brief window between mount and session hydration. Instead, we
 * resolve auth inline and return a safe empty summary on failure.
 */
export const getUsageSummary = createServerFn({ method: "GET" }).handler(
  async (): Promise<UsageSummary> => {
    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      return emptySummary();
    }

    const request = getRequest();
    const authHeader = request?.headers?.get("authorization");
    if (!authHeader?.startsWith("Bearer ")) {
      return emptySummary();
    }
    const token = authHeader.slice("Bearer ".length);
    if (!token) return emptySummary();

    const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      global: { headers: { Authorization: `Bearer ${token}` } },
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const { data: claimsResult, error: claimsError } = await supabase.auth.getClaims(token);
    const userId = claimsResult?.claims?.sub;
    if (claimsError || !userId) return emptySummary();

    try {
      const tier = await getUserTier(supabase, userId);
      const periodStart = new Date(
        Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1),
      )
        .toISOString()
        .slice(0, 10);

      const { data, error } = await supabase
        .from("usage_counters")
        .select("capability, count")
        .eq("user_id", userId)
        .eq("period_start", periodStart);

      if (error) {
        console.error("usage_counters select failed", error);
      }

      const counts = {} as Record<Capability, number>;
      for (const row of data ?? []) {
        counts[row.capability as Capability] = row.count;
      }

      return { tier, periodStart, counts };
    } catch (err) {
      console.error("getUsageSummary failed", err);
      return emptySummary();
    }
  },
);
